import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { staffAPI } from '../services/api';
import { customToast, toastMessages } from '../utils/toast';
import { useLanguage } from '../contexts/LanguageContext';
import { LimitGate } from '../components/FeatureGate';
import {
  Plus,
  Edit,
  Trash2,
  Users,
  Mail,
  User,
  Shield,
  Search,
  Filter,
  Sparkles,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

const StaffManagement = () => {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const { t, currentLanguage } = useLanguage(); // Add currentLanguage for debugging

  useEffect(() => {
    setIsVisible(true);

    // Debug: Check what translations are available
    console.log('=== STAFF MANAGEMENT DEBUG ===');
    console.log('Current Language:', currentLanguage);
    console.log('staff.header.badge:', t('staff.header.badge'));
    console.log('staff.header.title:', t('staff.header.title'));
    console.log('staff.stats.totalStaff:', t('staff.stats.totalStaff'));
    console.log('staff.roles.owner:', t('staff.roles.owner'));
    console.log('common.filter:', t('common.filter'));
    console.log('common.edit:', t('common.edit'));
    console.log('common.delete:', t('common.delete'));
    console.log('======================');
  }, [currentLanguage, t]);

  const queryClient = useQueryClient();

  // Fetch staff members
  const { data: staffData, isLoading } = useQuery(
    'staff',
    () => staffAPI.getStaff()
  );

  const staffMembers = staffData?.data || [];

  // Create staff mutation
  const createStaffMutation = useMutation(
    (data) => staffAPI.createStaff(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('staff');
        customToast.success(toastMessages.staffCreated);
        setShowModal(false);
      },
      onError: (error) => {
        customToast.error(toastMessages.createError('staff member'));
      },
    }
  );

  // Update staff mutation
  const updateStaffMutation = useMutation(
    (data) => staffAPI.updateStaff(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('staff');
        customToast.success(toastMessages.staffUpdated);
        setShowModal(false);
        setEditingStaff(null);
      },
      onError: (error) => {
        customToast.error(toastMessages.updateError('staff member'));
      },
    }
  );

  // Delete staff mutation
  const deleteStaffMutation = useMutation(
    (data) => staffAPI.deleteStaff(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('staff');
        customToast.success(toastMessages.staffDeleted);
        setShowDeleteConfirm(false);
        setSelectedStaff(null);
      },
      onError: (error) => {
        customToast.error(toastMessages.deleteError('staff member'));
        setShowDeleteConfirm(false);
        setSelectedStaff(null);
      },
    }
  );

  // Simple translation function that always returns strings
  const safeTranslate = (key, fallback) => {
    const result = t(key);
    console.log(`Translation for "${key}":`, result); // Debug each translation

    // If we get the key back, translation wasn't found
    if (result === key) {
      console.warn(`Translation not found for key: ${key}, using fallback: ${fallback}`);
      return fallback;
    }

    // If we get an object (shouldn't happen with our translation function), use fallback
    if (typeof result === 'object') {
      console.error(`Translation returned object for key: ${key}`, result);
      return fallback;
    }

    return result || fallback;
  };

  const handleCreateStaff = (data) => {
    createStaffMutation.mutate(data);
  };

  const handleUpdateStaff = (data) => {
    updateStaffMutation.mutate(data);
  };

  const handleDeleteStaff = (staff) => {
    setSelectedStaff(staff);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (selectedStaff) {
      deleteStaffMutation.mutate({ id: selectedStaff.id });
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setSelectedStaff(null);
  };

  // Filter staff based on search
  const filteredStaff = staffMembers.filter(staff =>
    staff.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'staff': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Shield className="h-4 w-4" />;
      case 'staff': return <User className="h-4 w-4" />;
      default: return <User className="h-4 w-4" />;
    }
  };

  const getRoleText = (role) => {
    return role === 'owner'
      ? safeTranslate('staff.roles.owner', 'Owner')
      : safeTranslate('staff.roles.staff', 'Staff Member');
  };

  const stats = [
    {
      label: safeTranslate('staff.stats.totalStaff', 'Total Staff'),
      value: staffMembers.length,
      color: 'blue',
      description: safeTranslate('staff.stats.allTeamMembers', 'All team members')
    },
    {
      label: safeTranslate('staff.stats.owners', 'Owners'),
      value: staffMembers.filter(s => s.role === 'owner').length,
      color: 'purple',
      description: safeTranslate('staff.stats.restaurantOwners', 'Restaurant owners')
    },
    {
      label: safeTranslate('staff.stats.staffMembers', 'Staff Members'),
      value: staffMembers.filter(s => s.role === 'staff').length,
      color: 'green',
      description: safeTranslate('staff.stats.teamMembers', 'Team members')
    },
    {
      label: safeTranslate('staff.stats.activeUsers', 'Active Users'),
      value: staffMembers.length,
      color: 'green',
      description: safeTranslate('staff.stats.currentlyActive', 'Currently active')
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-7">
      {/* Animated Background */}
      <div className="absolute inset-0 -z-10 h-full w-full bg-white bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:6rem_4rem]">
        <div className="absolute bottom-0 left-0 right-0 top-0 bg-[radial-gradient(circle_800px_at_50%_100px,#f0f9ff,transparent)]"></div>
      </div>

      <div className="container py-8">
        {/* Header */}
        <div className={`mb-8 transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          <div className="flex items-center gap-3 mb-2">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
              <Sparkles className="h-4 w-4" />
              {safeTranslate('staff.header.badge', 'Staff Management')}
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            {safeTranslate('staff.header.title', 'Manage Your Team')}
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl">
            {safeTranslate('staff.header.subtitle', 'Add, edit, and manage staff members who can access your restaurant dashboard.')}
          </p>
        </div>

        {/* Stats Overview */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 transition-all duration-700 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          {stats.map((stat, index) => {
            const colorClasses = {
              blue: 'from-blue-500 to-cyan-500',
              purple: 'from-purple-500 to-violet-500',
              green: 'from-green-500 to-emerald-500'
            };
            return (
              <div
                key={index}
                className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 border border-gray-100"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-r ${colorClasses[stat.color]} text-white group-hover:scale-110 transition-transform duration-300`}>
                    <Users className="h-6 w-6" />
                  </div>
                </div>

                <div>
                  <p className="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
                  <p className="text-sm font-medium text-gray-600 mb-2">{stat.label}</p>
                  <p className="text-xs text-gray-500">{stat.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions Bar */}
        <div className={`bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8 transition-all duration-700 delay-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={safeTranslate('staff.search.placeholder', 'Search staff by name, email, or role...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 w-full"
              />
            </div>

            {/* Action Buttons */}
            <LimitGate
              type="staff"
              current={staffMembers.length}
            >
              <div className="flex items-center gap-3">
                <button className="flex items-center gap-2 bg-gray-50 px-4 py-3 rounded-xl border border-gray-200 hover:bg-gray-100 transition-all duration-300">
                  <Filter className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium">
                    {safeTranslate('common.filters', 'Filter')}
                  </span>
                </button>

                <button
                  onClick={() => {
                    setEditingStaff(null);
                    setShowModal(true);
                  }}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2"
                >
                  <Plus className="h-5 w-5" />
                  {safeTranslate('staff.actions.addStaff', 'Add Staff Member')}
                </button>
              </div>
            </LimitGate>
          </div>
        </div>

        {/* Staff List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredStaff.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-lg border border-gray-100">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Users className="h-10 w-10 text-blue-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {searchTerm
                ? safeTranslate('staff.emptyState.noResults', 'No Staff Members Found')
                : safeTranslate('staff.emptyState.noStaff', 'No Staff Members Yet')
              }
            </h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              {searchTerm
                ? safeTranslate('staff.emptyState.noResultsDesc', 'No staff members match your search. Try adjusting your search terms.')
                : safeTranslate('staff.emptyState.noStaffDesc', 'Get started by adding your first staff member to help manage your restaurant.')
              }
            </p>
            {!searchTerm && (
              <button
                onClick={() => {
                  setEditingStaff(null);
                  setShowModal(true);
                }}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2 mx-auto"
              >
                <Plus className="h-5 w-5" />
                {safeTranslate('staff.actions.addFirstStaff', 'Add Your First Staff Member')}
              </button>
            )}
          </div>
        ) : (
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-700 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
            {filteredStaff.map((staff) => (
              <div
                key={staff.id}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 border border-gray-100"
              >
                <div className="p-6">
                  {/* Staff Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center text-white text-lg font-semibold">
                        {staff.name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">
                          {staff.name || safeTranslate('staff.unnamed', 'Unnamed Staff')}
                        </h3>
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {staff.email}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Role and Status */}
                  <div className="flex items-center justify-between mb-6">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${getRoleColor(staff.role)}`}>
                      {getRoleIcon(staff.role)}
                      {getRoleText(staff.role)}
                    </span>
                    <div className="flex items-center gap-1 text-green-600 text-sm">
                      <CheckCircle className="h-4 w-4" />
                      {safeTranslate('staff.status.active', 'Active')}
                    </div>
                  </div>

                  {/* Member Since */}
                  <div className="text-sm text-gray-500 mb-4">
                    {safeTranslate('staff.memberSince', 'Member since')} {staff.created_at ? new Date(staff.created_at).toLocaleDateString() : safeTranslate('common.unknown', 'Unknown')}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => {
                        setEditingStaff(staff);
                        setShowModal(true);
                      }}
                      className="flex-1 py-2 px-3 bg-blue-50 text-blue-700 rounded-lg font-medium hover:bg-blue-100 transition-all duration-300 flex items-center justify-center gap-1 border border-blue-200"
                    >
                      <Edit className="h-4 w-4" />
                      <span className="text-sm">
                        {safeTranslate('common.edit', 'Edit')}
                      </span>
                    </button>
                    {staff.role !== 'owner' && (
                      <button
                        onClick={() => handleDeleteStaff(staff)}
                        className="flex-1 py-2 px-3 bg-red-50 text-red-700 rounded-lg font-medium hover:bg-red-100 transition-all duration-300 flex items-center justify-center gap-1 border border-red-200"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="text-sm">
                          {safeTranslate('common.delete', 'Delete')}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Staff Modal */}
        {showModal && (
          <StaffModal
            staff={editingStaff}
            onClose={() => {
              setShowModal(false);
              setEditingStaff(null);
            }}
            onSubmit={editingStaff ? handleUpdateStaff : handleCreateStaff}
            isLoading={createStaffMutation.isLoading || updateStaffMutation.isLoading}
            safeTranslate={safeTranslate}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && selectedStaff && (
          <DeleteConfirmationModal
            staff={selectedStaff}
            onCancel={cancelDelete}
            onConfirm={confirmDelete}
            isLoading={deleteStaffMutation.isLoading}
            safeTranslate={safeTranslate}
          />
        )}
      </div>
    </div>
  );
};

// Staff Modal Component
const StaffModal = ({ staff, onClose, onSubmit, isLoading, safeTranslate }) => {
  const [formData, setFormData] = useState({
    name: staff?.name || '',
    email: staff?.email || '',
    role: staff?.role || 'staff',
    password: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { ...formData };

    if (staff && !data.password) {
      delete data.password;
    }

    if (staff) {
      data.id = staff.id;
    }

    onSubmit(data);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl border border-gray-100">
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center text-white mx-auto mb-4">
            <Users className="h-6 w-6" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900">
            {staff
              ? safeTranslate('staff.modal.editTitle', 'Edit Staff Member')
              : safeTranslate('staff.modal.addTitle', 'Add Staff Member')
            }
          </h3>
          <p className="text-gray-600 mt-2">
            {staff
              ? safeTranslate('staff.modal.editDescription', 'Update staff member details')
              : safeTranslate('staff.modal.addDescription', 'Invite a new team member to your restaurant')
            }
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                {safeTranslate('staff.form.nameLabel', 'Full Name *')}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="block w-full px-4 py-3 bg-white border border-gray-200 rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-gray-300"
                placeholder={safeTranslate('staff.form.namePlaceholder', 'Enter full name')}
                required
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {safeTranslate('staff.form.emailLabel', 'Email Address *')}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="block w-full px-4 py-3 bg-white border border-gray-200 rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-gray-300"
                placeholder={safeTranslate('staff.form.emailPlaceholder', 'Enter email address')}
                required
              />
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                {safeTranslate('staff.form.roleLabel', 'Role *')}
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="block w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-gray-300"
              >
                <option value="staff">{safeTranslate('staff.roles.staff', 'Staff Member')}</option>
                <option value="owner">{safeTranslate('staff.roles.owner', 'Owner')}</option>
              </select>
              <p className="text-xs text-gray-500 mt-2">
                {safeTranslate('staff.form.roleDescription', 'Owners have full access to all restaurant settings and data.')}
              </p>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                {staff
                  ? safeTranslate('staff.form.passwordEditLabel', 'New Password (leave blank to keep current)')
                  : safeTranslate('staff.form.passwordLabel', 'Password *')
                }
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="block w-full px-4 py-3 bg-white border border-gray-200 rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-gray-300"
                placeholder={safeTranslate('staff.form.passwordPlaceholder', 'Enter password')}
                required={!staff}
                minLength="6"
              />
              <p className="text-xs text-gray-500 mt-2">
                {safeTranslate('staff.form.passwordDescription', 'Password must be at least 6 characters long.')}
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all duration-300 border border-gray-200"
            >
              {safeTranslate('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {staff
                    ? safeTranslate('staff.modal.updating', 'Updating...')
                    : safeTranslate('staff.modal.creating', 'Creating...')
                  }
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  {staff
                    ? safeTranslate('staff.modal.updateButton', 'Update Staff')
                    : safeTranslate('staff.modal.addButton', 'Add Staff Member')
                  }
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Confirmation Modal Component 
const DeleteConfirmationModal = ({ staff, onCancel, onConfirm, isLoading, safeTranslate }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl border border-gray-100">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4">
            <AlertTriangle className="h-8 w-8" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            {safeTranslate('staff.deleteConfirmation.title', 'Remove Staff Member?')}
          </h3>
          <p className="text-gray-600 mb-2">
            {safeTranslate('staff.deleteConfirmation.message', 'Are you sure you want to remove {name} from your staff?').replace('{name}', staff.name)}
          </p>
          <p className="text-sm text-red-600 font-medium">
            {safeTranslate('staff.deleteConfirmation.warning', 'This action cannot be undone. They will lose access to the restaurant dashboard.')}
          </p>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all duration-300 border border-gray-200"
          >
            {safeTranslate('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 py-3 px-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                {safeTranslate('staff.deleteConfirmation.removing', 'Removing...')}
              </>
            ) : (
              <>
                <Trash2 className="h-5 w-5" />
                {safeTranslate('staff.deleteConfirmation.removeStaff', 'Remove Staff')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StaffManagement;